import { getApp, getApps, initializeApp } from 'firebase/app';
import type { Firestore } from 'firebase/firestore';
import type { FirebaseStorage } from 'firebase/storage';

import { FIREBASE_CONFIG, FIRESTORE_ENABLED, STORAGE_ENABLED } from '@/config/env';

let firestore: Firestore | null = null;
let storage: FirebaseStorage | null = null;

export function firebaseApp() {
  return getApps().length ? getApp() : initializeApp(FIREBASE_CONFIG);
}

export async function db(): Promise<Firestore> {
  if (!FIRESTORE_ENABLED) {
    throw new Error('Firestore is not configured — guard callers with FIRESTORE_ENABLED.');
  }
  if (!firestore) {
    const { getFirestore } = await import('firebase/firestore');
    firestore = getFirestore(firebaseApp());
  }
  return firestore;
}

/** Loads the Storage SDK on first use, for the same reason. */
async function bucket(): Promise<FirebaseStorage> {
  if (!STORAGE_ENABLED) {
    throw new Error('Cloud Storage is not configured — guard callers with STORAGE_ENABLED.');
  }
  if (!storage) {
    const { getStorage } = await import('firebase/storage');
    storage = getStorage(firebaseApp());
  }
  return storage;
}

/**
 * Resolves one stored object name to a URL an `<img>` can load.
 *
 * The API returns object names, never URLs. Firebase Storage authorises the read
 * with the caller's own ID token under `storage.rules`, so nothing is signed and
 * no URL outlives the access that produced it.
 */
export async function objectUrl(objectName: string): Promise<string> {
  const { getDownloadURL, ref } = await import('firebase/storage');
  return getDownloadURL(ref(await bucket(), objectName));
}

/** Stores one picked file under a name the caller chose. `storage.rules`
 *  refuses every overwrite, so the name must be one nothing holds yet. */
export async function uploadObject(objectName: string, file: File): Promise<void> {
  const { ref, uploadBytes } = await import('firebase/storage');
  await uploadBytes(ref(await bucket(), objectName), file);
}

/**
 * Every Firestore path this app reads.
 *
 * Each tenant path is a function of the organization id, because the tenant
 * boundary is the document path — a forgotten scope is a missing argument rather
 * than a query that quietly returns another client's data.
 */
export const collections = {
  users: 'users',
  user: (email: string) => `users/${email}`,
  organization: (organizationId: string) => `organizations/${organizationId}`,
  settings: (organizationId: string) => `organizations/${organizationId}/settings`,
  branding: (organizationId: string) => `organizations/${organizationId}/settings/branding`,
  campaigns: (organizationId: string) => `organizations/${organizationId}/campaigns`,
  assets: (organizationId: string, campaignId: string) =>
    `organizations/${organizationId}/campaigns/${campaignId}/assets`,
  anomalies: (organizationId: string, campaignId: string) =>
    `organizations/${organizationId}/campaigns/${campaignId}/anomalies`,
  generalSettings: (organizationId: string) => `organizations/${organizationId}/settings/general`,
  brands: (organizationId: string) => `organizations/${organizationId}/brands`,
  templates: (organizationId: string) => `organizations/${organizationId}/templates`,
  generatedAssets: (organizationId: string) => `organizations/${organizationId}/generated_assets`,
  products: (organizationId: string, brandSlug: string) =>
    `organizations/${organizationId}/brands/${brandSlug}/products`,
  brandAssets: (organizationId: string, brandSlug: string) =>
    `organizations/${organizationId}/brands/${brandSlug}/assets`,
  brandProductImports: (organizationId: string, brandSlug: string) =>
    `organizations/${organizationId}/brands/${brandSlug}/product_imports`,
} as const;
